/*
 * Copyright (c) 2024. Bolmate B.V.
 */

import React, {MouseEventHandler, PropsWithChildren, useContext, useEffect, useState} from 'react';
import {BasicMessage, SystemMessageType} from "../../util/interfaces";
import {AppContext} from "./appContext";
import {BASIC_MESSAGES_KEY} from "../../util/constants";
import User from '../../models/userModel';
import InfoMessage from "./messages/infoMessage";
import WarningMessage from "./messages/warningMessage";
import ErrorMessage from "./messages/errorMessage";

interface Props {
    onClose?: MouseEventHandler;
    message: BasicMessage;
    persistent?: true;
    className?: string;
    fallback?: React.ReactElement;
}

export default function BasicMessage(props: PropsWithChildren<Props>) {
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {persistent, message, onClose, className, fallback, children} = props;
    const [visible, setVisible] = useState(false);

    function _onClose(evt: React.MouseEvent) {
        setVisible(false);
        updateFlex();
        onClose?.(evt);
    }

    function setVisibility() {
        if (!user) return setVisible(false);
        const seenMessages = user.getFlex(BASIC_MESSAGES_KEY);
        if (seenMessages && Array.isArray(seenMessages) && seenMessages.includes(message.id))
            return setVisible(false);
        setVisible(true);
    }

    function updateFlex() {
        if (!user) return;
        let seenMessages = user.getFlex(BASIC_MESSAGES_KEY);
        if (seenMessages && Array.isArray(seenMessages))
            seenMessages.push(message.id);
        else seenMessages = [message.id];
        user.setFlex(BASIC_MESSAGES_KEY, seenMessages);
        user.update(undefined, (user: User, success: boolean) => {
            if (success) context.onChangeContext({user});
        });
    }

    useEffect(() => {
        setVisibility();
    }, [context.userContext.user]);

    if (!visible) return fallback ?? null;
    const onCloseHandler = persistent ? undefined : _onClose;
    if (!message.messageType || message.messageType === SystemMessageType.INFO)
        return <InfoMessage className={className} onClose={onCloseHandler}>{children}</InfoMessage>;
    else if (message.messageType === SystemMessageType.WARNING)
        return <WarningMessage className={className} onClose={onCloseHandler}>{children}</WarningMessage>;
    else if (message.messageType === SystemMessageType.ERROR)
        return <ErrorMessage className={className} onClose={onCloseHandler}>{children}</ErrorMessage>;
    else throw TypeError(`Message type ${message.messageType} is not supported as basic message`);
}